import React, { useEffect, useState, useContext } from "react";
import { PrimaryButton, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import { t } from "i18next";
import loaderContext from "../../context/loader/LoaderContext";
import toastContext from "../../context/toast/ToastContext";
import { TOAST_TYPE } from "../../constants/Constants";
import { getActiveSessions, logoutSession } from "../../service/UserService";
import { normalizeTimeZone } from "../IssuesAndTroubleshooting/helper/helper";
import moment from "moment-timezone";

const ActiveSessions = () => {
  const { ref, setLoaderRunning } = useContext(loaderContext);
  const { showToastv2 } = useContext(toastContext);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false); // 🆕 state for "logout all" button

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoaderRunning(true);
      ref.current.continuousStart();
      const response = await getActiveSessions();

      if (
        response?.status === 200 &&
        response.data?.result &&
        response.data.result.toLowerCase() === "success"
      ) {
        setSessions(response.data.data || []);
      } else {
        showToastv2(TOAST_TYPE.ERROR, t("active-sessions.failed-to-load"));
      }
    } catch (error) {
      showToastv2(TOAST_TYPE.ERROR, t("active-sessions.failed-to-load"));
    } finally {
      setLoaderRunning(false);
      ref.current.complete();
      setLoading(false);
    }
  };

  const handleLogoutSession = async (token) => {
    try {
      setLoaderRunning(true);
      ref.current.continuousStart();
      const response = await logoutSession(token);

      if (
        response?.status === 200 &&
        response.data?.result &&
        response.data.result.toLowerCase() === "success"
      ) {
        showToastv2(
          TOAST_TYPE.SUCCESS,
          t("active-sessions.session-logged-out")
        );
        await fetchSessions(); // Refresh list
      } else {
        showToastv2(TOAST_TYPE.ERROR, t("active-sessions.logout-failed"));
      }
    } catch (error) {
      showToastv2(TOAST_TYPE.ERROR, t("active-sessions.logout-failed"));
    } finally {
      setLoaderRunning(false);
      ref.current.complete();
    }
  };

  const getLocaleDateTimeFromUTC = (date) => {
  if (!date) return "No Date";

  const rawUserTz = localStorage.getItem("userTimeZone");
  const userTz = normalizeTimeZone(rawUserTz);
  const systemTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const displayTz = userTz || systemTz;

  // Parse as UTC
  const momentDate = moment.utc(date);

  if (!momentDate.isValid()) return "No Date";

  return momentDate
    .tz(displayTz)
    .format("MM/DD/YYYY, h:mm:ss A");
};

  // 🆕 Logout all sessions except current one
  const handleLogoutAllOthers = async () => {
    try {
      setLogoutAllLoading(true);
      setLoaderRunning(true);
      ref.current.continuousStart();

      const otherSessions = sessions.filter((s) => !s.currentSession);

      // Execute sequentially (can be parallel too)
      for (const session of otherSessions) {
        await logoutSession(session.token);
      }

      showToastv2(
        TOAST_TYPE.SUCCESS,
        t("active-sessions.logged-out-all-others")
      );
      await fetchSessions();
    } catch (error) {
      showToastv2(TOAST_TYPE.ERROR, t("active-sessions.logout-failed"));
    } finally {
      setLogoutAllLoading(false);
      setLoaderRunning(false);
      ref.current.complete();
    }
  };

  return (
    <div className="tab-section">
      <section
        className="sectionA"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <p
          style={{
            fontSize: "20px",
            fontWeight: "600",
            padding: "0px",
          }}
        >
          {t("active-sessions.title")}
        </p>

        {/* 🆕 Logout Others Button */}
        {sessions.length > 1 && (
          <PrimaryButton
            text={
              logoutAllLoading
                ? t("active-sessions.logging-out")
                : t("active-sessions.logout-others")
            }
            onClick={handleLogoutAllOthers}
            disabled={logoutAllLoading}
            // styles={{
            //   root: {
            //     backgroundColor: "#dc2626",
            //     borderColor: "#dc2626",
            //     color: "#fff",
            //     fontSize: "13px",
            //     height: "32px",
            //     lineHeight: "32px",
            //     padding: "0 16px",
            //     borderRadius: "4px",
            //   },
            //   rootHovered: {
            //     backgroundColor: "#b91c1c",
            //     borderColor: "#b91c1c",
            //   },
            // }}
          />
        )}
      </section>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: "40px" }}>
          <Spinner
            size={SpinnerSize.large}
            label={t("active-sessions.loading")}
          />
        </div>
      ) : (
        <div
          className="custom-drilldown-studylist-modal-scroll"
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
            overflowX: "auto",
            width: "100%",
            border: "1px solid #334155",
            borderRadius: "4px",
            marginTop: "15px",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              backgroundColor: "#1e293b",
              color: "#fff",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#334155" }}>
                <th
                  className="table-cell-head"
                  style={{
                    minWidth: "260px",
                    padding: "10px",
                    textAlign: "left",
                  }}
                >
                  {t("active-sessions.device")}
                </th>
                <th
                  className="table-cell-head"
                  style={{ minWidth: "140px", padding: "10px" }}
                >
                  {t("active-sessions.ip")}
                </th>
                <th
                  className="table-cell-head"
                  style={{ minWidth: "200px", padding: "10px" }}
                >
                  {t("active-sessions.logged-in")}
                </th>
                <th
                  className="table-cell-head"
                  style={{ minWidth: "200px", padding: "10px" }}
                >
                  {t("active-sessions.expires")}
                </th>
                <th
                  className="table-cell-head"
                  style={{ minWidth: "160px", padding: "10px" }}
                >
                  {t("active-sessions.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: "20px", color: "#94a3b8" }}>
                    {t("active-sessions.no-active-sessions")}
                  </td>
                </tr>
              ) : (
                [...sessions]
                  .sort(
                    (a, b) =>
                      (b.currentSession ? 1 : 0) - (a.currentSession ? 1 : 0)
                  )
                  .map((session, index) => (
                    <tr
                      key={index}
                      style={{
                        borderTop: "1px solid #334155",
                        backgroundColor: session.currentSession
                          ? "#374151"
                          : index % 2 === 0
                          ? "#1e293b"
                          : "#2d3a4f",
                      }}
                    >
                      <td
                        className="table-cell"
                        style={{
                          padding: "10px",
                          verticalAlign: "middle",
                          wordBreak: "break-word",
                          fontWeight: session.currentSession ? "600" : "normal",
                        }}
                      >
                        {session.deviceInfo ||
                          t("active-sessions.unknown-device")}
                        {session.currentSession && (
                          <span
                            style={{
                              color: "#22c55e",
                              fontSize: "12px",
                              marginLeft: "8px",
                            }}
                          >
                            ({t("active-sessions.current-session")})
                          </span>
                        )}
                      </td>
                      <td className="table-cell" style={{ padding: "10px" }}>
                        {session.ipAddress || "-"}
                      </td>
                      <td className="table-cell" style={{ padding: "10px" }}>
                        {getLocaleDateTimeFromUTC(session.createdAt)}
                      </td>
                      <td className="table-cell" style={{ padding: "10px" }}>
                        {session.expiresAt
                          ? getLocaleDateTimeFromUTC(session.expiresAt)
                          : "-"}
                      </td>
                      <td className="table-cell" style={{ padding: "10px" }}>
                        {!session.currentSession && (
                          <PrimaryButton
                            text={t("active-sessions.logout")}
                            onClick={() => handleLogoutSession(session.token)}
                            // styles={{
                            //   root: {
                            //     backgroundColor: "#dc2626",
                            //     borderColor: "#dc2626",
                            //     color: "#fff",
                            //     fontSize: "13px",
                            //     height: "32px",
                            //     lineHeight: "32px",
                            //     padding: "0 16px",
                            //     borderRadius: "4px",
                            //   },
                            //   rootHovered: {
                            //     backgroundColor: "#b91c1c",
                            //     borderColor: "#b91c1c",
                            //   },
                            // }}
                          />
                        )}
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActiveSessions;
